import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMyAccount, useGetMyProfile } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import H2 from "@/components/H2";
import Spacer from "@/components/Spacer";
import { processKycStatuses } from "@/helpers/kycStatus";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import RoyaltiesData from "./_royaltiesData";
import RoyaltiesFilter from "./_royaltiesFilter";

function Royalties() {
  const navigate = useNavigate();
  const { t } = useTranslation("other");
  const kyc = useAppStore.getState()?.kyc;
  const { showKyc, showKycPending } = processKycStatuses(kyc);
  const profile = useGetMyProfile();
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const { data: siteConfig } = useSiteConfigAuth();
  const selectedAccount = useAppStore(s => s.selectedAccount)?.trim() || "";

  const myAccount = useGetMyAccount({ artistaccountcode: selectedAccount });

  const [appliedFilters, setAppliedFilters] = useState(null);

  const hideRoyalties
    = siteConfig?.disable_royalties
      || profile?.data?.hideroyalties
      || showKyc
      || showKycPending
      || (selectedAccount && myAccount?.data?.hideroyalties);

  useEffect(() => {
    if (hideRoyalties)
      navigate({ to: "/" });
  }, [hideRoyalties, navigate]);

  useEffect(() => {
    if (!selectedAccount) {
      setAppliedFilters(null);
      return;
    }
    const dateTo = new Date();
    const dateFrom = new Date(dateTo);
    dateFrom.setFullYear(dateFrom.getFullYear() - 5);
    setAppliedFilters({ accounts: [selectedAccount], writers: [], songs: [], dateFrom, dateTo });
  }, [selectedAccount]);

  const defaultDateRange = useMemo(() => {
    if (!selectedAccount)
      return undefined;
    const to = new Date();
    const from = new Date(to);
    from.setFullYear(from.getFullYear() - 5);
    return { from, to };
  }, [selectedAccount]);

  const handleSearch = useCallback((filters) => {
    setAppliedFilters(filters);
  }, []);

  return (
    <div className="flex flex-wrap">
      <div className="w-full lg:min-h-screen-minus-header">
        <div className="mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40">
          <H2>{t("other:royalties-header")}</H2>
          <Spacer />
          <div className={`${isTunecore ? "bg-layer-inverse" : "bg-white"} rounded-lg p-4 lg:p-8`}>
            <RoyaltiesFilter
              defaultAccount={selectedAccount || null}
              onSearch={handleSearch}
              initialDateRange={defaultDateRange}
            />
            <RoyaltiesData appliedFilters={appliedFilters} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Royalties;
